import { useState } from 'react';
import { Link } from 'react-router';
import { askAi } from '../components/AskAi.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, EmptyState, Skeleton, StatusBadge, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { docLink } from '../lib/approvals.ts';
import { day, money, todayIso } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useAction } from './manage/common.tsx';
import { Kpi } from './Reports.tsx';

/**
 * AI Insights: what in the books needs a look. The checks run on the server without AI, so the
 * page is exact and always there; "Ask AI" hands any of it to the Finance agent to explain.
 */

type Severity = 'high' | 'medium' | 'low';
interface DocRef {
  kind: 'invoice' | 'bill';
  id: string;
  name: string;
  date: string;
  amount: number;
}
type Anomaly =
  | { kind: 'duplicate_bill'; severity: Severity; party: string; docs: DocRef[] }
  | { kind: 'large_bill'; severity: Severity; party: string; docs: DocRef[]; usual: number }
  | { kind: 'overdue_invoice' | 'overdue_bill'; severity: Severity; party: string; docs: DocRef[]; days: number }
  | { kind: 'cash_short'; severity: Severity; week: string; balance: number };
interface CashWeek {
  start: string;
  end: string;
  inflow: number;
  outflow: number;
  balance: number;
}
interface Cash {
  today: string;
  opening: number;
  weeks: CashWeek[];
  overdue_in: number;
  overdue_in_count: number;
  overdue_out: number;
  overdue_out_count: number;
  lowest: { week: string; balance: number };
  short_from: string | null;
}
type VatFinding =
  | { kind: 'not_posted'; severity: Severity; count: number; docs: DocRef[] }
  | { kind: 'vat_mismatch'; severity: Severity; documents: number; books: number }
  | { kind: 'zero_rate_lines'; severity: Severity; count: number; docs: DocRef[] }
  | { kind: 'bills_without_vat'; severity: Severity; count: number; amount: number; docs: DocRef[] };
interface Vat {
  from: string;
  to: string;
  sales_vat: number;
  purchase_vat: number;
  posted_sales_vat: number | null;
  findings: VatFinding[];
}
interface Insights {
  cash: Cash | null;
  anomalies: Anomaly[];
  vat: Vat | null;
}

const SEVERITY: Record<Severity, { tone: Tone; icon: IconName; label: MessageKey }> = {
  high: { tone: 'danger', icon: 'alert', label: 'ins.sev_high' },
  medium: { tone: 'warn', icon: 'info', label: 'ins.sev_medium' },
  low: { tone: 'info', icon: 'info', label: 'ins.sev_low' },
};

function anomalyText(a: Anomaly): { title: string; body: string; ask: string } {
  switch (a.kind) {
    case 'duplicate_bill': {
      const [x, y] = a.docs;
      return {
        title: t('ins.dup_title', { party: a.party }),
        body: t('ins.dup_body', { a: x!.name, b: y!.name, amount: money(y!.amount) }),
        ask: `Are ${x!.name} and ${y!.name} from ${a.party} a duplicate? Check both bills and tell me what to do.`,
      };
    }
    case 'large_bill':
      return {
        title: t('ins.large_title', { party: a.party }),
        body: t('ins.large_body', { name: a.docs[0]!.name, amount: money(a.docs[0]!.amount), usual: money(a.usual) }),
        ask: `Bill ${a.docs[0]!.name} from ${a.party} is much larger than usual. Check it and tell me whether it looks right.`,
      };
    case 'overdue_invoice':
      return {
        title: t('ins.late_in_title', { party: a.party }),
        body: t('ins.late_in_body', { name: a.docs[0]!.name, amount: money(a.docs[0]!.amount), days: a.days }),
        ask: `${a.party} is ${a.days} days late paying ${a.docs[0]!.name}. What is their full balance and history, and how should I follow up?`,
      };
    case 'overdue_bill':
      return {
        title: t('ins.late_out_title', { party: a.party }),
        body: t('ins.late_out_body', { name: a.docs[0]!.name, amount: money(a.docs[0]!.amount), days: a.days }),
        ask: `We are ${a.days} days late paying ${a.party} (${a.docs[0]!.name}). Can we afford to pay it now, given the cash forecast?`,
      };
    case 'cash_short':
      return {
        title: t('ins.short_title'),
        body: t('ins.short_body', { week: day(a.week), amount: money(a.balance) }),
        ask: 'The cash forecast goes below zero. Explain why, and what I can do: which customers to chase and which payments could wait.',
      };
  }
}

function vatText(f: VatFinding): { title: string; body: string } {
  switch (f.kind) {
    case 'not_posted':
      return { title: tn('ins.vat_not_posted', f.count), body: t('ins.vat_not_posted_body') };
    case 'vat_mismatch':
      return { title: t('ins.vat_mismatch'), body: t('ins.vat_mismatch_body', { docs: money(f.documents), books: money(f.books) }) };
    case 'zero_rate_lines':
      return { title: tn('ins.vat_zero', f.count), body: t('ins.vat_zero_body') };
    case 'bills_without_vat':
      return { title: tn('ins.vat_no_vat_bills', f.count), body: t('ins.vat_no_vat_bills_body', { amount: money(f.amount) }) };
  }
}

function Docs({ docs }: { docs: DocRef[] }) {
  if (!docs.length) return null;
  return (
    <ul className="ins-docs" aria-label={t('ins.documents')}>
      {docs.map((d) => (
        <li key={d.id}>
          <Link to={docLink(d.kind === 'invoice' ? 'invoicing.invoice' : 'purchase.bill', d.id)} className="ins-doc">
            <Icon name={d.kind === 'invoice' ? 'invoice' : 'receipt'} size={14} />
            <span className="ins-doc-name">{d.name}</span>
            <span className="muted">{day(d.date)}</span>
            <span className="num">{money(d.amount)}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function Insights() {
  const { can } = useSession();
  const [month, setMonth] = useState(todayIso().slice(0, 7));
  const { data, reload } = useAction<Insights>('ai.insights', { month });
  const [refreshing, setRefreshing] = useState(false);
  const refresh = async () => {
    setRefreshing(true);
    await reload();
    setRefreshing(false);
  };
  const ai = can('ai.use');

  return (
    <Shell>
      <div className="page reports insights">
        <div className="page-head">
          <div>
            <h1>{t('ins.title')}</h1>
            <p className="muted">{t('ins.subtitle')}</p>
          </div>
          <div className="page-actions">
            <Button icon="repeat" busy={refreshing} onClick={() => void refresh()}>
              {t('ins.refresh')}
            </Button>
            {ai ? (
              <Button variant="primary" icon="sparkle" onClick={() => askAi('Review my AI Insights: the anomalies, the cash forecast and the VAT check. What should I deal with first, and why?')}>
                {t('ins.ask_review')}
              </Button>
            ) : null}
          </div>
        </div>
        {!data ? (
          <div className="ins-loading">
            <Skeleton h={140} r="var(--radius-lg)" />
            <Skeleton h={280} r="var(--radius-lg)" />
          </div>
        ) : (
          <>
            <Attention items={data.anomalies} />
            {data.cash ? <CashForecast cash={data.cash} /> : null}
            {data.vat ? <VatCheck vat={data.vat} month={month} onMonth={setMonth} /> : null}
            {!data.cash && !data.vat ? <EmptyState icon="lock" title={t('ins.no_access')} body={t('ins.no_access_body')} /> : null}
          </>
        )}
      </div>
    </Shell>
  );
}

function Attention({ items }: { items: Anomaly[] }) {
  return (
    <section className="panel ins-section" aria-labelledby="ins-attention">
      <div className="ins-section-head">
        <h2 id="ins-attention" className="section-title">
          {t('ins.attention')}
        </h2>
        {items.length ? <span className="ins-count">{items.length}</span> : null}
      </div>
      {!items.length ? (
        <p className="ins-clear">
          <Icon name="checkCircle" size={18} />
          {t('ins.nothing_unusual')}
        </p>
      ) : (
        <ul className="ins-list">
          {items.map((a, i) => {
            const s = SEVERITY[a.severity];
            const text = anomalyText(a);
            return (
              <li key={i} className={`ins-item sev-${a.severity}`}>
                <div className="ins-item-main">
                  <div className="ins-item-head">
                    <StatusBadge tone={s.tone} icon={s.icon} label={t(s.label)} />
                    <strong>{text.title}</strong>
                  </div>
                  <p>{text.body}</p>
                  {'docs' in a ? <Docs docs={a.docs} /> : null}
                </div>
                <Button size="sm" icon="sparkle" className="ins-ask" onClick={() => askAi(text.ask)}>
                  {t('ins.ask')}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function CashForecast({ cash }: { cash: Cash }) {
  const end = cash.weeks.at(-1)?.balance ?? cash.opening;
  const top = Math.max(1, ...cash.weeks.flatMap((w) => [w.inflow, w.outflow]));
  const fmt = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return (
    <section className="panel ins-section" aria-labelledby="ins-cash">
      <div className="ins-section-head">
        <h2 id="ins-cash" className="section-title">
          {t('ins.cash_title', { n: cash.weeks.length })}
        </h2>
        <Button size="sm" icon="sparkle" className="ins-ask" onClick={() => askAi('Walk me through the cash forecast for the next weeks: what comes in, what goes out, the lowest point and what I should do about it.')}>
          {t('ins.ask')}
        </Button>
      </div>
      <div className="rep-kpis">
        <Kpi label={t('ins.cash_today')} value={money(cash.opening)} hint={t('ins.cash_today_hint')} warn={cash.opening < 0} />
        <Kpi label={t('ins.cash_lowest')} value={money(cash.lowest.balance)} hint={t('ins.cash_week_of', { week: day(cash.lowest.week) })} warn={cash.lowest.balance < 0} />
        <Kpi label={t('ins.cash_end', { n: cash.weeks.length })} value={money(end)} hint={t('ins.cash_end_hint')} warn={end < 0} />
      </div>
      <figure className="ins-chart" aria-label={t('ins.cash_chart')}>
        <div className="ins-legend" aria-hidden="true">
          <span className="ins-key in">{t('ins.coming_in')}</span>
          <span className="ins-key out">{t('ins.going_out')}</span>
        </div>
        <div className="ins-chart-plot">
          {cash.weeks.map((w) => {
            const label = fmt.format(new Date(`${w.start}T00:00:00Z`));
            const h = (v: number) => `${v ? Math.max(2, (v / top) * 100) : 0}%`;
            return (
              <span key={w.start} className="ins-col" title={`${label}: +${money(w.inflow)} / -${money(w.outflow)} = ${money(w.balance)}`}>
                <span className="ins-pos">
                  <span className="ins-bar" style={{ height: h(w.inflow) }} />
                </span>
                <span className="ins-neg">
                  <span className="ins-bar neg" style={{ height: h(w.outflow) }} />
                </span>
              </span>
            );
          })}
        </div>
        <div className="ins-chart-axis" aria-hidden="true">
          {cash.weeks.map((w, i) => (
            <span key={w.start}>{i % 2 === 0 ? fmt.format(new Date(`${w.start}T00:00:00Z`)) : ''}</span>
          ))}
        </div>
      </figure>
      <div className="table-wrap" role="region" aria-label={t('ins.cash_table')} tabIndex={0}>
        <table className="data-table ins-weeks">
          <thead>
            <tr>
              <th scope="col">{t('ins.week')}</th>
              <th scope="col">{t('ins.coming_in')}</th>
              <th scope="col">{t('ins.going_out')}</th>
              <th scope="col">{t('ins.balance')}</th>
            </tr>
          </thead>
          <tbody>
            {cash.weeks.map((w) => (
              <tr key={w.start} className={w.balance < 0 ? 'neg' : undefined}>
                <td>{day(w.start)}</td>
                <td>{w.inflow ? money(w.inflow) : '—'}</td>
                <td>{w.outflow ? money(-w.outflow) : '—'}</td>
                <td className="strong">{money(w.balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="ins-note muted">
        <Icon name="info" size={14} />
        {t('ins.cash_note')}
        {cash.overdue_in_count ? ` ${tn('ins.cash_overdue_in', cash.overdue_in_count, { amount: money(cash.overdue_in) })}` : ''}
        {cash.overdue_out_count ? ` ${tn('ins.cash_overdue_out', cash.overdue_out_count, { amount: money(cash.overdue_out) })}` : ''}
      </p>
    </section>
  );
}

function VatCheck({ vat, month, onMonth }: { vat: Vat; month: string; onMonth: (m: string) => void }) {
  const due = vat.sales_vat - vat.purchase_vat;
  return (
    <section className="panel ins-section" aria-labelledby="ins-vat">
      <div className="ins-section-head">
        <h2 id="ins-vat" className="section-title">
          {t('ins.vat_title')}
        </h2>
        <div className="ins-head-actions">
          <input type="month" className="input ins-month" value={month} max={todayIso().slice(0, 7)} onChange={(e) => e.target.value && onMonth(e.target.value)} aria-label={t('ins.month')} />
          <Button size="sm" icon="sparkle" className="ins-ask" onClick={() => askAi(`Check our VAT for ${month} before we file: sales VAT, purchase VAT, what is due, and anything that looks wrong.`)}>
            {t('ins.ask')}
          </Button>
        </div>
      </div>
      <div className="rep-kpis">
        <Kpi label={t('ins.vat_sales')} value={money(vat.sales_vat)} hint={vat.posted_sales_vat === null ? t('ins.vat_from_invoices') : t('ins.vat_in_books', { amount: money(vat.posted_sales_vat) })} />
        <Kpi label={t('ins.vat_purchases')} value={money(vat.purchase_vat)} hint={t('ins.vat_from_bills')} />
        <Kpi label={due >= 0 ? t('ins.vat_due') : t('ins.vat_refund')} value={money(Math.abs(due))} hint={t('ins.vat_due_hint')} />
      </div>
      {!vat.findings.length ? (
        <p className="ins-clear">
          <Icon name="checkCircle" size={18} />
          {t('ins.vat_clear')}
        </p>
      ) : (
        <ul className="ins-list">
          {vat.findings.map((f) => {
            const s = SEVERITY[f.severity];
            const text = vatText(f);
            return (
              <li key={f.kind} className={`ins-item sev-${f.severity}`}>
                <div className="ins-item-main">
                  <div className="ins-item-head">
                    <StatusBadge tone={s.tone} icon={s.icon} label={t(s.label)} />
                    <strong>{text.title}</strong>
                  </div>
                  <p>{text.body}</p>
                  {'docs' in f ? <Docs docs={f.docs} /> : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
